// src/pages/BalancesLayout.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from "axios";
import config from '../config/config';

import FlashBar from "../components/FlashBar";
import NavigationBar from "../components/NavigationBar";
import HeroSection from "../components/HeroSection";
import Banner from "../components/Banner";
import CategorySection from "../components/Categories";
import ProductSection from "../components/ProductSection";
import Testimonials from "../components/Testimonials";
import Cta from "../components/Cta";
import ModalSection from "../components/ModalSection";

import CategoriesNav from "../components/CategoriesNav";
import Faqs from "../components/Faqs";
import Christmas from "../components/ChristmasWidgets";
import CartSection from "../components/CartSection";
import FooterSection from "../components/FooterSection";
import ProductView from "../components/ProductView";
import SearchWidget from "../components/SearchWidget";
import { useQuery } from '@tanstack/react-query'; 
import { fetchLayoutSettings } from '../libs/apis/LayoutSettingsApi';

import { 
  LayoutDashboard, Zap, Grid, ShoppingBag, Footprints, 
  Image as ImageIcon, Star, Plus, X, RefreshCw 
} from 'lucide-react';


// Admin editors (lazy loaded)
const FlashBarAdmin    = React.lazy(() => import("../pages/FlashBar"));
const NavigationAdmin  = React.lazy(() => import("../pages/NavigationBar"));
const HeroAdmin        = React.lazy(() => import("../pages/HeroAdmin"));
const BannerAdmin      = React.lazy(() => import("../pages/BannerAdmin"));
const CategoryAdmin    = React.lazy(() => import("../pages/CategoryAdmin"));
const ProductAdmin     = React.lazy(() => import("../pages/ProductAdmin"));
const CartAdmin        = React.lazy(() => import("../pages/CartAdmin"));
const FooterAdmin      = React.lazy(() => import("../pages/FooterAdmin"));
const SearchAdmin      = React.lazy(() => import("../pages/SearchAdmin"));
const TestimonialsAdmin      = React.lazy(() => import("../pages/TestimonialsAdmin"));
const ModalAdmin      = React.lazy(() => import("../pages/ModalAdmin"));
const CtaAdmin      = React.lazy(() => import("../pages/CtaAdmin"));
const CategoriesNavAdmin      = React.lazy(() => import("../pages/CategoriesNavAdmin"));

const FaqAdmin      = React.lazy(() => import("../pages/FaqAdmin"));
const ChristmasAdmin      = React.lazy(() => import("../pages/ChristmasAdmin"));
const ProductViewAdmin = React.lazy(() => import("../pages/ProductView"));

// ──────────────────────────────────────────────────────────────────────────────
// DEFAULTS - embedded in bundle for instant render
// ──────────────────────────────────────────────────────────────────────────────

// =============================================
//  YuMart Site Configuration (Prefilled from DB)
//  Sections: flashbar, navbar, banner, categories, products, testimonials, faqs, footer
//  Current Date: January 22, 2026
// =============================================

const DEFAULT_LAYOUT_ORDER = [
 // "flashbar",           // active flashbar with countdown
  "navbar",             // dark-luxury / premium navbar
  "categoriesnav-2",
 // "banner",             // full-width banner with confetti
  "categories",         // carousel of categories
  "products",           // main products carousel (Discounted Deals Today)
  "hero-2", 
 // "testimonials-2",     // customer testimonials
  "faqs-2",             // FAQ section
  //"christmas-2",
  //"marketingmodals-2",
  //
  "footer"              // comprehensive footer
];

const DEFAULT_GLOBAL_CONFIGS = {

  // ─────────────────────────────────────────────
  // id: 1    slug: flashbar     is_active: 0     updated: 2026-01-21 21:49:03
  // ─────────────────────────────────────────────
  flashbar: {
    enabled: true,
    preset: "premium-dark",
    dismissible: false,
    devMode: true,
    messages: [
      {
        text: "Trusted With Orders - Enjoy Wholesale Deals!",
        countdownEnd: "2026-01-21T00:51:00.000Z"
      }
    ],
    customStyles: {
      backgroundColor: "#3B4883",
      textColor: "#ffffff",
      ctaColor: "#ebebeb",
      height: "48px",
      prefixTextColor: "#e6e6ff",
      prefixIcon: "Sparkles",
      prefixIconColor: "#ff8000"
    }
  },

  // ─────────────────────────────────────────────
  // id: 3    slug: bonus        is_active: 1     updated: 2025-12-18 01:10:39
  // ─────────────────────────────────────────────
  bonus: {
    navpreset: "minimal-centered",
    logo: "MINIM",
    menuItems: [
      { label: "New", href: "#" },
      { label: "Women", href: "#" },
      { label: "Men", href: "#" },
      { label: "Sale", href: "#" }
    ],
    topBarText: "Free shipping on orders $50+",
    cartCount: 2,
    devMode: true,
    customStyles: {
      topBarTextColor: "white",
      topBarNavBackground: "orange",
      backgroundColor: "#40248b",
      textColor: "#ffffff",
      logoColor: "#c5c5c5",
      iconColor: "#ffffff",
      cartBackgroundColor: "orange",
      cartButtonColor: "white"
    }
  },

  // ─────────────────────────────────────────────
  // id: 4    slug: banner       is_active: 0     updated: 2026-01-26 15:40:56
  // ─────────────────────────────────────────────
  banner: {
    bannerpreset: "asymmetric",
    autoPlay: true,
    interval: 5000,
    confetti: {
      enabled: true,
      trigger: "onSlideChange",
      particleCount: 100,
      spread: 70,
      originY: 0.6,
      colors: "#ff718b,#fdba74,#a78bfa,#72ddf7",
      ticks: 200,
      gravity: 0.8,
      decay: 0.94,
      scalar: 1,
      shapes: "square,circle"
    },
    slides: [
      {
        id: 1769057820261,
        title: null,
        subtitle: null,
        description: null,
        ctaText: null,
        ctaLink: "/",
        background: "bg-gradient-to-br from-gray-50 to-gray-200",
        image: "",
        imageOverlayColor: "black",
        imageOverlayOpacity: 20,
        contentPadding: "p-8",
        titleStyle: {
          color: "#111827",
          fontSize: "text-xl",
          fontWeight: "font-medium",
          fontFamily: "font-sans"
        },
        subtitleStyle: {
          color: "#111827",
          fontSize: "text-sm",
          fontWeight: "font-normal",
          fontFamily: "font-sans"
        },
        descriptionStyle: {
          color: "#111827",
          fontSize: "text-base",
          fontWeight: "font-normal",
          fontFamily: "font-sans"
        },
        ctaStyle: {
          bgColor: "bg-gray-900",
          textColor: "text-white"
        }
      }
    ],
    devMode: false,
    customStyles: {
      container: {
        padding: "p-4 md:p-10",
        margin: null,
        borderRadius: "rounded-lg",
        boxShadow: null
      }
    }
  },

  // ─────────────────────────────────────────────
  // id: 6    slug: layout       is_active: 0     updated: 2026-02-01 21:53:21
  // This is the current active component order
  // ─────────────────────────────────────────────
  layout: [
   // "flashbar",
    "navbar",
    //"categoriesnav-2",
    "hero",
    "categories",
    "products-4",
    
    "products-2",
    "testimonials-2",
    "faqs-2",
    "marketingmodals-2",
    "footer"
  ],

  // ─────────────────────────────────────────────
  // id: 7    slug: products     is_active: 0     updated: 2026-01-29 21:57:25
  // ─────────────────────────────────────────────
  products: {
    preset: "carousel",
    title: "Discounted Deals Today",
    subtitle: "Discover our best-selling items",
    products: [
      {
        id: 3,
        name: "3 - Fold Umbrella - Black",
        price: 360,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/aohWTm3UlGOmwqzXPbxpwkGdCJ2zqnjrrmUXSdUK.jpg",
        badge: null,
        inStock: false
      },
      {
        id: 6,
        name: "3-fold Umbrella - Maroon",
        price: 360,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/SrjUQaUaYcDsCYshhz2FE021tWDcdcjQG3aDroxM.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 9,
        name: "5 Panel Polyester Plain Cap - Baby Pink",
        price: 140,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/bchwqKFx6rVwnNKjVcWCyqlZo2ldJwM4OE7uz6m4.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 10,
        name: "5 Panel Polyester Plain Cap - Beige",
        price: 140,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/cFsIhdOXxXvm1MAEMm2Blh2R4O9dooWKJLWSJr8M.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 2,
        name: "2 Fold Umbrella - Black",
        price: 360,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/TpBNZzBoXfrioCJCMdnraaiSgOaARepK9aSo9015.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 4,
        name: "3-fold Umbrella - Brown",
        price: 360,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/GV18totUH57eXq7RvJkCTiP0sVh40cJZktmqTp4S.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 8,
        name: "3-fold Umbrella - Purple",
        price: 360,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/mlgJDSXmUZvJVgy7oK6QQQ1hjXKBH7IZLTvbwuVY.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 11,
        name: "5 Panel Polyester Plain Cap - Black",
        price: 140,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/HycmZ00LcqUw0ijKQrb2iiPdXKNcGSNgeXPSicPx.jpg",
        badge: null,
        inStock: true
      }
    ],
    customStyles: {
      container: {
        padding: "p-4 md:p-10",
        margin: null,
        borderRadius: "rounded-lg",
        boxShadow: null,
        background: "#ffffff",
        titleColor: "black",
        subtitleColor: "black",
        featuredBg: "rgb(240, 90, 42)",
        accentColor: "#ff8040",
        activeBg: "rgb(191, 58, 13)",
        cardBackground: "bg-white",
        textColor: "#000000",
        textAlign: "center",
        fontFamily: "poppins",
        titlefontSize: "text-4xl md:text-5xl",
        titlefontWeight: "font-bold"
      }
    },
    grid: {
      mobileCols: 2,
      desktopCols: 6
    },
    imageSize: {
      width: 200,
      height: 160
    },
    cardHeight: 470,
    productLinkPattern: "/products/{id}",
    devMode: true
  },

  // ─────────────────────────────────────────────
  // id: 8    slug: hero         is_active: 0     updated: 2026-01-29 21:48:03
  // ─────────────────────────────────────────────
  hero: {
    preset: "product-showcase",
    title: "Discover Your Perfect Style",
    subtitle: "Explore our latest collection of premium fashion pieces crafted for the modern individual.",
    badge: "New Collection 2026",
    ctaPrimary: {
      text: "Shop Now",
      link: "/shop"
    },
    ctaSecondary: {
      text: "View Details",
      link: "#"
    },
    stats: [
      { value: "500+", label: "Products" },
      { value: "50k+", label: "Happy Customers" },
      { value: "4.9", label: "Avg Rating" }
    ],
    product: {
      id: 2,
      name: "2 Fold Umbrella - Black",
      price: 360,
      originalPrice: null,
      image: "https://admins.junisaproducts.com/images/product/TpBNZzBoXfrioCJCMdnraaiSgOaARepK9aSo9015.jpg",
      rating: 4.8,
      reviews: 0
    },
    devMode: true,
    customStyles: {
      container: {
        padding: "py-0 md:py-2",
        background: "#272d4e"
      },
      titleColor: "#f4f1f1",
      subtitleColor: "#fbf4f4",
      badgeColor: "bg-gray-100 text-gray-900",
      ctaPrimaryBg: "#f05a2a",
      ctaSecondaryBorder: "#ffffff",
      heroImage: "https://sample-videos.com/video123/mp4/720/big_buck_bunny_720p_1mb.mp4",
      backgroundImage: "https://sample-videos.com/video123/mp4/720/big_buck_bunny_720p_1mb.mp4",
      ctaPrimaryText: "#ffffff",
      badgeText: "#ffffff",
      badgeBg: "#f05a2a"
    }
  },

  // ─────────────────────────────────────────────
  // id: 9    slug: footer       is_active: 0     updated: 2026-02-01 21:59:06
  // ─────────────────────────────────────────────
 footer: {
  preset: "minimal-clean",                    // ← Changed from "comprehensive"
  brandName: "JUNISA",
  brandDesc: "Premium quality meets modern design. Your destination for timeless style.",
  newsletterTitle: "Stay in the Loop",
  newsletterDesc: "Subscribe to get special offers, free giveaways, and exclusive deals.",
  ctaText: "Subscribe",
  socialLinks: ["Facebook", "Instagram", "Twitter", "Youtube"],
  shopLinks: ["Baseball Caps", "Hats", "Umbrella", "Hand Bags"],
  serviceLinks: ["Contact Us", "Help Center", "Our Stores", "FAQ"],
  companyLinks: ["Our Stores"],
  contact: {
    email: "orders@junisaproducts.com",
    phone: "+254 705 572 556",
    address: "Nairobi, Kamukunji, Jubilee Complex Shop G8"
  },
  trustBadges: ["Secure Payment", "Fast Shipping"],
  paymentIcons: ["Mpesa"],
  bottomText: "© 2026 JUNISA. All rights reserved.",
  bottomLegalLinks: [],
  customStyles: {
    container: {
      padding: "py-12 md:py-16",
      background: "bg-gray-900",
      textColor: "rgb(240,90,42)",
      linkColor: "#ffffff",
      linkHover: "#ffffff",
      newsletterBg: "bg-gray-800"
    }
  },
  columns: {
    mobileCols: 2,
    desktopCols: 4
  },
  devMode: true
},

  // ─────────────────────────────────────────────
  // id: 10   slug: cart         is_active: 0     updated: 2026-01-13 01:22:00
  // ─────────────────────────────────────────────
  cart: {
    activeDesign: "sidebar",
    title: "Your Cart",
    emptyMessage: "Your cart is empty. Start shopping!",
    continueShoppingText: "Continue Shopping",
    checkoutText: "Proceed to Checkout",
    devMode: false,
    customStyles: {
      background: "bg-white",
      textColor: "text-gray-900",
      buttonBg: "bg-gray-900",
      buttonText: "text-white"
    }
  },

  // ─────────────────────────────────────────────
  // id: 11   slug: search       is_active: 0     updated: 2026-01-29 15:57:38
  // ─────────────────────────────────────────────
  search: {
    activeDesign: "design2",
    placeholder: "Search products, brands, categories...",
    devMode: false,
    customStyles: {
      background: null,
      textColor: null
    }
  },

  // ─────────────────────────────────────────────
  // id: 12   slug: product-view is_active: 0     updated: 2026-01-29 22:25:55
  // ─────────────────────────────────────────────
  "product-view": {
    activeDesign: "design2",
    devMode: false,
    customStyles: {
      background: null,
      textColor: null
    }
  },

  // ─────────────────────────────────────────────
  // id: 37   slug: categories   is_active: 0     updated: 2026-02-01 21:45:19
  // ─────────────────────────────────────────────
  categories: {
    preset: "floating-circles",
    title: "Our popular collections",
    subtitle: null,
    categories: [
      {
        id: 6,
        name: "Umbrella",
        count: 0,
        image: "",
        icon: "ShoppingBag",
        iconColor: "from-indigo-500 to-purple-600",
        displayMode: "image",
        titleStyle: {
          color: "#000000",
          fontSize: "text-6xl md:text-7xl",
          fontWeight: "font-medium",
          fontFamily: "font-sans"
        },
        subtitleStyle: {
          color: "#4B5563",
          fontSize: "text-xl",
          fontWeight: "font-normal",
          fontFamily: "font-sans"
        }
      },
      {
        id: 32,
        name: "Reflector Jackets",
        items: "0",
         count: 0,
        image: null,
        icon: "ShoppingBag",
        iconColor: "from-indigo-500 to-purple-600",
        displayMode: "icon",
        titleStyle: {
          color: "#000000",
          fontSize: "text-6xl md:text-7xl",
          fontWeight: "font-medium",
          fontFamily: "font-sans"
        },
        subtitleStyle: {
          color: "#4B5563",
          fontSize: "text-xl",
          fontWeight: "font-normal",
          fontFamily: "font-sans"
        }
      },
      {
        id: 47,
        name: "Towels",
         count: 0,
        items: "0",
        image: null,
        icon: "ShoppingBag",
        iconColor: "from-indigo-500 to-purple-600",
        displayMode: "icon",
        titleStyle: {
          color: "#000000",
          fontSize: "text-6xl md:text-7xl",
          fontWeight: "font-medium",
          fontFamily: "font-sans"
        },
        subtitleStyle: {
          color: "#4B5563",
          fontSize: "text-xl",
          fontWeight: "font-normal",
          fontFamily: "font-sans"
        }
      },{
        id: 1,
        name: "Bags",
         count: 0,
        items: "0",
        image: null,
        icon: "ShoppingBag",
        iconColor: "from-indigo-500 to-purple-600",
        displayMode: "icon",
        titleStyle: {
          color: "#000000",
          fontSize: "text-6xl md:text-7xl",
          fontWeight: "font-medium",
          fontFamily: "font-sans"
        },
        subtitleStyle: {
          color: "#4B5563",
          fontSize: "text-xl",
          fontWeight: "font-normal",
          fontFamily: "font-sans"
        }
      },{
        id: 3,
        name: "Hats",
         count: 0,
        items: "0",
        image: null,
        icon: "ShoppingBag",
        iconColor: "from-indigo-500 to-purple-600",
        displayMode: "icon",
        titleStyle: {
          color: "#000000",
          fontSize: "text-6xl md:text-7xl",
          fontWeight: "font-medium",
          fontFamily: "font-sans"
        },
        subtitleStyle: {
          color: "#4B5563",
          fontSize: "text-xl",
          fontWeight: "font-normal",
          fontFamily: "font-sans"
        }
      },{
        id: 2,
        name: "Caps",
        items: "0",
         count: 0,
        image: null,
        icon: "ShoppingBag",
        iconColor: "from-indigo-500 to-purple-600",
        displayMode: "icon",
        titleStyle: {
          color: "#000000",
          fontSize: "text-6xl md:text-7xl",
          fontWeight: "font-medium",
          fontFamily: "font-sans"
        },
        subtitleStyle: {
          color: "#4B5563",
          fontSize: "text-xl",
          fontWeight: "font-normal",
          fontFamily: "font-sans"
        }
      },{
        id: 7,
        name: "Christmas Decorations",
        items: "0",
        image: null,
         count: 0,
        icon: "ShoppingBag",
        iconColor: "from-indigo-500 to-purple-600",
        displayMode: "icon",
        titleStyle: {
          color: "#000000",
          fontSize: "text-6xl md:text-7xl",
          fontWeight: "font-medium",
          fontFamily: "font-sans"
        },
        subtitleStyle: {
          color: "#4B5563",
          fontSize: "text-xl",
          fontWeight: "font-normal",
          fontFamily: "font-sans"
        }
      }
    ],
    displayMode: "icon",
    grid: {
      mobileCols: 3,
      desktopCols: 6
    },
    imageSize: {
      width: 5,
      height: 5
    },
    cardHeight: 380,
    cardBorderRadius: "rounded-full",
    cardStyles: {
      background: "bg-gradient-to-br",
      border: "border-none",
      shadow: "shadow-2xl",
      hoverScale: "hover:scale-110 hover:rotate-2",
      padding: "p-10",
      transition: "all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)"
    },
    titleStyle: {
      color: "#000000",
      fontSize: "text-4xl md:text-5xl",
      fontWeight: "font-bold",
      fontFamily: "font-sans"
    },
    subtitleStyle: {
      color: "#000000",
      fontSize: "text-xl",
      fontWeight: "font-medium",
      fontFamily: "font-sans"
    },
    headerAlignment: "text-center",
    devMode: true,
    customStyles: {
      container: {
        padding: "py-6 md:py-32  px-10",
        background: "bg-white",
        titleColor: "text-white",
        subtitleColor: "text-white",
        headerTitlePosition: "left",
        titleFontSize: "text-4xl md:text-5xl",
        margin: "mx-auto max-w-9xl",
        fontSize: "2",
        accentColor: "green",
        fontFamily: "poppins",
        textColor: "#111827",
        featuredBg: "bg-white",
        activeBg: "bg-gray-900",
        borderRadius: "10px",
        cardBackground: "bg-white",
        textAlign: "center"
      }
    }
  },

  // ─────────────────────────────────────────────
  // id: 48   slug: navbar       is_active: 0     updated: 2026-01-29 21:35:08
  // ─────────────────────────────────────────────
  navbar: {
    navpreset: "blue-modern",
    logoText: "unisa",
    logoSvgColor: "#ffffff",
    logoSize: 40,
    logoTextVisible: true,
    logoTextSize: "text-xl",
    logoTextMargin: "ml-0",
    logoMargin: "me-0",
    logoVisible: true,
    logoPath: "/storage/logos/SpI8ay9nm3oFusLwWTWHZLdXoQaf16xM3djWIt51.png",
    topBarText: "Free shipping on orders KSH 1000+",
    cartCount: 0,
    menuItems: [
      { label: "Home", href: "MainPage_s" },
      { label: "Shop", href: "shop" },
      { label: "Our Stores", href: "our-stores" },
      { label: "Help Center", href: "help-center" },
      { label: "Contact Us", href: "contact-us" }
    ],
    contactPhone: "(123) 456-6666",
    contactEmail: "contact@yumart.com",
    searchPlaceholder: "Search products...",
    categoryOptions: "All Categories,Electronics,Fashion,Home & Garden,Sports,Books,Toys,Beauty",
    allDepartmentsText: "ALL DEPARTMENTS",
    topBarLeftText: "Official Store Shop | Same-day dispatch",
    currencyOptions: "Quick Links,Home,Shop,Help Center,Contact Us",
    devMode: true,
    customStyles: {
      backgroundColor: "#f05a2a",
      textColor: "#ffffff",
      iconColor: "#ffffff",
      cartBackgroundColor: "orange",
      cartButtonColor: "#ffffff",
      topBarNavBackground: "linear-gradient(to right, #111827, #1F2937, #111827)",
      topBarTextColor: "white",
      searchBarGradient: "from-orange-500 to-orange-600",
      topInfoBarGradient: "from-orange-500 to-gray-900"
    }
  },

  // ─────────────────────────────────────────────
  // id: 53   slug: testimonials-2   is_active: 0   updated: 2026-02-01 21:54:33
  // ─────────────────────────────────────────────
  "testimonials-2": {
    title: "What Our Customers Say",
    subtitle: "Don't just take our word for it. Here's what our happy customers have to say.",
    testimonials: [
      {
        id: 1,
        name: "Stephen",
        role: null,
        location: null,
        image: "bg-gradient-to-br from-gray-100 to-gray-200",
        testimonial: "I bought this product and was of very good quality.",
        purchasedItem: "2 Fold Umbrella - Black",
        verified: true
      },
      {
        id: 2,
        name: "Kenn",
        role: null,
        location: null,
        image: "bg-gradient-to-br from-gray-100 to-gray-200",
        testimonial: "Very nice piece of item",
        purchasedItem: "3 - Fold Umbrella - Black",
        verified: true
      }
    ],
    devMode: true,
    customStyles: {
      container: {
        padding: "py-0 md:py-2",
        background: "bg-white"
      },
      titleColor: "#f05a2a",
      subtitleColor: "#f05a2a",
      badgeColor: "bg-gray-100 text-gray-900",
      ctaPrimaryBg: "bg-gray-900 text-white",
      ctaSecondaryBorder: "border-gray-900 text-gray-900",
      ctaBg: "#f05a2a",
      textColor: "#f05a2a",
      starColor: "#ff8000"
    }
  },

  // ─────────────────────────────────────────────
  // id: 54   slug: faqs-2       is_active: 0     updated: 2026-01-29 22:11:20
  // ─────────────────────────────────────────────
  "faqs-2": {
    title: "Frequently Asked Questions",
    subtitle: "Find answers to the most common questions about our products and services.",
    faqs: [
      {
        id: 10,
        question: "How do I contact customer support?",
        answer: "You can contact our customer support through live chat, email, or phone. Visit the \"Contact Us\" page for more details.",
        category: "general",
        isOpen: false
      },
      {
        id: 11,
        question: "How do I update my shipping address?",
        answer: "If your order has not been shipped yet, you can update your shipping address by going to your account settings or through the specific order details page.",
        category: "general",
        isOpen: false
      },
      {
        id: 12,
        question: "What do I do if I receive the wrong item?",
        answer: "If you receive an incorrect item, please report the issue through your order history, and we will assist you in exchanging the item or issuing a refund.",
        category: "general",
        isOpen: false
      },
      {
        id: 14,
        question: "How can I check the status of my refund?",
        answer: "You can check the status of your refund by logging into your account and visiting the \"Returns & Refunds\" section. There, you’ll see details on any pending refunds.",
        category: "general",
        isOpen: false
      }
    ],
    devMode: true,
    customStyles: {
      container: {
        padding: "py-0 md:py-2",
        background: "#f05a2a"
      },
      titleColor: "#ffffff",
      subtitleColor: "#ffffff",
      badgeColor: "bg-gray-100 text-gray-900",
      ctaPrimaryBg: "bg-gray-900 text-white",
      ctaSecondaryBorder: "border-gray-900 text-gray-900",
      ctaBg: "#ff8000",
      textColor: "#ff8000",
      starColor: "#ff8000"
    }
  },

  // ─────────────────────────────────────────────
  // id: 55   slug: flashbar-2   is_active: 0     updated: 2026-01-22 05:13:41
  // ─────────────────────────────────────────────
  "flashbar-2": {
    enabled: true,
    preset: "premium-dark",
    dismissible: false,
    devMode: true,
    messages: [
      {
        text: "Trusted With Orders - Enjoy Wholesale Deals!",
        countdownEnd: "2026-01-21T00:51:00.000Z"
      }
    ],
    customStyles: {
      backgroundColor: "#3B4883",
      textColor: "#ffffff",
      ctaColor: "#ebebeb",
      height: "48px",
      prefixTextColor: "#e6e6ff",
      prefixIcon: "Sparkles",
      prefixIconColor: "#ff8000"
    }
  },

  // ─────────────────────────────────────────────
  // id: 56   slug: flashbar-3   is_active: 0     updated: 2026-01-22 05:15:23
  // ─────────────────────────────────────────────
  "flashbar-3": {
    enabled: true,
    preset: "premium-dark",
    dismissible: false,
    devMode: true,
    messages: [
      {
        text: "Trusted With Orders - Enjoy Wholesale Deals!",
        countdownEnd: "2026-01-21T00:51:00.000Z"
      }
    ],
    customStyles: {
      backgroundColor: "#3B4883",
      textColor: "#ffffff",
      ctaColor: "#ebebeb",
      height: "48px",
      prefixTextColor: "#e6e6ff",
      prefixIcon: "Sparkles",
      prefixIconColor: "#ff8000",
      note: "This is a copy"
    }
  },

  // ─────────────────────────────────────────────
  // id: 57   slug: christmas-2  is_active: 0     updated: 2026-01-22 06:56:58
  // ─────────────────────────────────────────────
  "christmas-2": {
    preset: "design1",
    title: "Frequently Asked Questions",
    subtitle: "Find answers to the most common questions about our products and services.",
    buttonText: "Shop Now",
    countdownDate: "2026-12-25T00:00:00",
    showSnow: true,
    showStars: true,
    customStyles: {
      container: {
        padding: "py-0 md:py-2",
        background: "#f05a2a"
      },
      titleColor: "#ffffff",
      subtitleColor: "#e6e6e5",
      badgeColor: "bg-gray-100 text-gray-900",
      ctaPrimaryBg: "bg-gray-900 text-white",
      ctaSecondaryBorder: "border-gray-900 text-gray-900",
      ctaBg: "#ff8000",
      textColor: "#ff8000",
      starColor: "#ff8000"
    },
    devMode: true
  },

  // ─────────────────────────────────────────────
  // id: 58   slug: flashbar-4   is_active: 0     updated: 2026-01-25 17:34:57
  // ─────────────────────────────────────────────
  "flashbar-4": {
    enabled: true,
    preset: "premium-dark",
    dismissible: false,
    devMode: true,
    messages: [
      {
        text: "Trusted With Orders - Enjoy Wholesale Deals!",
        countdownEnd: "2026-01-21T00:51:00.000Z"
      }
    ],
    customStyles: {
      backgroundColor: "#3B4883",
      textColor: "#ffffff",
      ctaColor: "#ebebeb",
      height: "48px",
      prefixTextColor: "#e6e6ff",
      prefixIcon: "Sparkles",
      prefixIconColor: "#ff8000",
      note: "This is a copy"
    }
  },

  // ─────────────────────────────────────────────
  // id: 59   slug: categoriesnav-2   is_active: 0   updated: 2026-02-01 22:08:18
  // ─────────────────────────────────────────────
  "categoriesnav-2": {
  preset: "elegant-pastel",
  categories: [
    {
      name: "Farmers",
      icon: null,
      submenu: ["Bucket Hat - White", "Two In One Bucket Hats - Nike Pink"],
      featured: false
    },
    {
      name: "Wamuchomba",
      icon: null,
      submenu: ["Air Hostess Cap - Maroon", "Air Hostess Cap - Gold", "Air Hostess Cap - Navy Blue"],
      featured: false
    },
    {
      name: "Walking Stick Umbrella",
      icon: null,
      submenu: ["2 Fold Umbrella - Black"],
      featured: false
    },
    {
      name: "Maua Ya Nywele",
      icon: null,
      submenu: ["Birdcage Veil Fascinator - Hot Pink", "Birdcage Veil Fascinator - Red"],
      featured: true     // ← I kept it true because it was true in your original
    },
    {
      name: "Toppa",
      icon: "⭐",           // ← converted from \u2b50 to actual star
      submenu: ["Round Cap - New Era A's"],
      featured: false
    }
  ],
  devMode: true,           // ← I kept true because it was true in original
  customStyles: {
    backgroundColor: "bg-white",
    textColor: "#ffffff",
    hoverBg: "hover:bg-purple-50",
    hoverText: "hover:text-purple-700",
    featuredBg: "from-orange-600 to-pink-600"
  }
},

  categoriesnav_2: {
    preset: "elegant-pastel",
    categories: [
      {
        name: "Farmers",
        icon: null,
        submenu: ["Bucket Hat - White", "Two In One Bucket Hats - Nike Pink"],
        featured: false
      },
      {
        name: "Wamuchomba",
        icon: null,
        submenu: ["Air Hostess Cap - Maroon", "Air Hostess Cap - Gold", "Air Hostess Cap - Navy Blue"],
        featured: false
      },
      {
        name: "Walking Stick Umbrella",
        icon: null,
        submenu: ["2 Fold Umbrella - Black"],
        featured: false
      },
      {
        name: "Maua Ya Nywele",
        icon: null,
        submenu: ["Birdcage Veil Fascinator - Hot Pink", "Birdcage Veil Fascinator - Red"],
        featured: false
      },
      {
        name: "Toppa",
        icon: "⭐",
        submenu: ["Round Cap - New Era A's"],
        featured: false
      }
    ],
    devMode: false,
    customStyles: []
  },

  // ─────────────────────────────────────────────
  // id: 64   slug: products-2   is_active: 0     updated: 2026-02-01 21:53:08
  // ─────────────────────────────────────────────
  "products-2": {
    preset: "classic-grid",
    title: "Recommended for You",
    subtitle: null,
    products: [
      {
        id: 558,
        name: "Meshed Trucker Hat - Art That Kills",
        price: 200,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/QSWHjxLzuZtCLAM36X7WNn8awY3gRhnsyY1BeyeT.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 1032,
        name: "Christmas Lights - Multicolour",
        price: 200,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/7OaJPLjtEJ8JCYkO71sFGHFYrFa2pMQpYDvKQubs.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 1103,
        name: "Herren Turban Satin Hat - Jungle Green",
        price: 600,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/hW62dK3cKUV0A0rWU5EK4e33LSS85oYza6cpioU1.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 37,
        name: "6 Panel Polyester Plain Cap - Green",
        price: 140,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/TvYpjqxF3v09uv8VVzui0Cg3kAMYQOGfK6l4RnZG.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 870,
        name: "Women Organza Kentucky Derby Hat - White",
        price: 450,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/jRhuvMUGr7TinwgvTtn9DRtrH0kaTa29ZMwOTtSW.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 471,
        name: "Kids Hiphop Cap - Boy",
        price: 200,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/3NrX1qSi2A7iozczruKt8BAwbLGDdbWsRuf2gsTw.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 381,
        name: "Girl Hat -orange",
        price: 90,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/Ipdq00ydxzHh1g9bVvOvAybTvvrFbF8ITyj4sl8b.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 919,
        name: "Carry On Duffel Bag - Dark Purple",
        price: 1000,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/63soS8MUkYHIHLL7bqDavXRVNervtY1kPRkLStL0.jpg",
        badge: null,
        inStock: true
      }
    ],
    customStyles: {
      container: {
        padding: "p-4 md:p-10",
        margin: null,
        borderRadius: "rounded-lg",
        boxShadow: null,
        background: "bg-orange-100",
        titleColor: "black",
        subtitleColor: "black",
        activeBg: null,
        cardBackground: "bg-white",
        titlefontSize: "text-4xl md:text-5xl",
        titlefontWeight: "font-bold",
        featuredBg: "rgb(240, 90, 42)",
        accentColor: "#f05a2a"
      }
    },
    grid: {
      mobileCols: 2,
      desktopCols: 6
    },
    imageSize: {
      width: 200,
      height: 160
    },
    cardHeight: 470,
    productLinkPattern: "/products/{id}",
    devMode: true
  },

  // ─────────────────────────────────────────────
  // id: 65   slug: products-3   is_active: 0     updated: 2026-02-01 21:52:33
  // ─────────────────────────────────────────────
  "products-3": {
    preset: "carousel",
    title: "Top Picks",
    subtitle: null,
    products: [
      {
        id: 2,
        name: "2 Fold Umbrella - Black",
        price: 360,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/TpBNZzBoXfrioCJCMdnraaiSgOaARepK9aSo9015.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 4,
        name: "3-fold Umbrella - Brown",
        price: 360,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/GV18totUH57eXq7RvJkCTiP0sVh40cJZktmqTp4S.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 11,
        name: "5 Panel Polyester Plain Cap - Black",
        price: 140,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/HycmZ00LcqUw0ijKQrb2iiPdXKNcGSNgeXPSicPx.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 21,
        name: "5 Panel Polyester Plain Cap - Maroon",
        price: 140,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/f6SJFrVmJcmAU9RFVEjlkrbCrSi21pCvQlact6o4.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 22,
        name: "5 Panel Polyester Plain Cap - Navy Blue",
        price: 140,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/Q2oj6Gha31tMfGpNPX0bW1VSVyg2Q3vIzze5e2OE.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 14,
        name: "5 Panel Polyester Plain Cap - Dark Green",
        price: 140,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/7ZHYnSKo3qlESzoQpD0tw1Ym2dbEo4fTdrXq1Fut.jpg",
        badge: null,
        inStock: true
      }
    ],
    customStyles: {
      container: {
        padding: "p-4 md:p-10",
        margin: null,
        borderRadius: "rounded-lg",
        boxShadow: null,
        background: "bg-gray-100",
        titleColor: "black",
        subtitleColor: "black",
        fontFamily: "Poppins",
        fontSize: "1em",
        featuredBg: "rgb(240, 90, 42)",
        activeBg: "green",
        accentColor: "#f05a2a",
        titlefontSize: "text-4xl md:text-5xl",
        titlefontWeight: "font-bold"
      }
    },
    grid: {
      mobileCols: 2,
      desktopCols: 6
    },
    imageSize: {
      width: 200,
      height: 160
    },
    cardHeight: 470,
    productLinkPattern: "/products/{id}",
    devMode: true
  },

  // ─────────────────────────────────────────────
  // id: 66   slug: hero-2       is_active: 0     updated: 2026-01-30 01:06:11
  // ─────────────────────────────────────────────
  "hero-2": {
    preset: "product-grid",
    title: "Discover Your Perfect Style",
    subtitle: "Explore our latest collection of premium fashion pieces crafted for the modern individual.",
    badge: "New Collection 2026",
    ctaPrimary: {
      text: "Shop Now",
      link: "/shop"
    },
    ctaSecondary: {
      text: "Watch Video",
      link: "#"
    },
    stats: [
      { value: "500+", label: "Products" },
      { value: "50k+", label: "Happy Customers" },
      { value: "4.9", label: "Avg Rating" }
    ],
    devMode: true,
    customStyles: {
      container: {
        padding: "py-0 md:py-2",
        background: "#272d4e"
      },
      titleColor: "#f4f1f1",
      subtitleColor: "#fbf4f4",
      badgeColor: "bg-gray-100 text-gray-900",
      ctaPrimaryBg: "bg-gray-900 text-white",
      ctaSecondaryBorder: "border-gray-900 text-gray-900"
    }
  },

  // ─────────────────────────────────────────────
  // id: 67   slug: products-4   is_active: 0     updated: 2026-02-01 21:51:01
  // ─────────────────────────────────────────────
  "products-4": {
    preset: "carousel",
    title: "Flash Sale",
    subtitle: null,
    products: [
      {
        id: 420,
        name: "Hip Hop Cap - Chicago Bull",
        price: 220,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/FzUO9PjH7cwiZr4J9IQbILlMTXMzcdlc2btqM1l4.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 64,
        name: "Al Bag - Royal Blue",
        price: 500,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/zTAfGEfWNjkKIaNwIt4chzcru51cbLWM24mNxBZi.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 393,
        name: "Half Cap Men - Orange",
        price: 140,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/zDSIMuuvC3SEmaUpQLpfQgICH9OlpNTZxj5DyKCu.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 507,
        name: "Light Life Tote Bag - Maroon",
        price: 450,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/W9UG7z1ZiU3U8NeBHgqUWLZ6bxD3UN8EqdQHNIUo.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 542,
        name: "Meshed Trucker Caps - Camouflage",
        price: 180,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/UBqep8VIxWOLfUsPikYsY6ktW28moMtKRfu6MHDF.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 109,
        name: "Baseball Cap - Volume Caps",
        price: 190,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/qEVJlcXt8EOXVy5R0WocWdUyE8MR8WIKMLgNozFl.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 613,
        name: "Plain Bag With Umbrella Space - Black",
        price: 350,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/0m3eDlywUXaUsL4pZTt2D33s2Pfr8YoloetGDltI.jpg",
        badge: null,
        inStock: true
      }
    ],
    customStyles: {
      container: {
        padding: "p-4 md:p-10",
        margin: null,
        borderRadius: "rounded-lg",
        boxShadow: null,
        background: "bg-gray-100",
        titleColor: "black",
        subtitleColor: "black",
        fontFamily: "Poppins",
        fontSize: "1em",
        featuredBg: "rgb(240, 90, 42)",
        activeBg: "green",
        accentColor: "#f05a2a",
        titlefontSize: "text-4xl md:text-5xl",
        titlefontWeight: "font-bold"
      }
    },
    grid: {
      mobileCols: 2,
      desktopCols: 6
    },
    imageSize: {
      width: 200,
      height: 160
    },
    cardHeight: 470,
    productLinkPattern: "/products/{id}",
    devMode: true
  },

  // ─────────────────────────────────────────────
  // id: 68   slug: products-5   is_active: 0     updated: 2026-02-01 21:53:21
  // ─────────────────────────────────────────────
  "products-5": {
    preset: "carousel",
    title: "Flash Sale",
    subtitle: null,
    products: [
      {
        id: 420,
        name: "Hip Hop Cap - Chicago Bull",
        price: 220,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/FzUO9PjH7cwiZr4J9IQbILlMTXMzcdlc2btqM1l4.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 64,
        name: "Al Bag - Royal Blue",
        price: 500,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/zTAfGEfWNjkKIaNwIt4chzcru51cbLWM24mNxBZi.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 393,
        name: "Half Cap Men - Orange",
        price: 140,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/zDSIMuuvC3SEmaUpQLpfQgICH9OlpNTZxj5DyKCu.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 507,
        name: "Light Life Tote Bag - Maroon",
        price: 450,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/W9UG7z1ZiU3U8NeBHgqUWLZ6bxD3UN8EqdQHNIUo.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 542,
        name: "Meshed Trucker Caps - Camouflage",
        price: 180,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/UBqep8VIxWOLfUsPikYsY6ktW28moMtKRfu6MHDF.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 109,
        name: "Baseball Cap - Volume Caps",
        price: 190,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/qEVJlcXt8EOXVy5R0WocWdUyE8MR8WIKMLgNozFl.jpg",
        badge: null,
        inStock: true
      },
      {
        id: 613,
        name: "Plain Bag With Umbrella Space - Black",
        price: 350,
        originalPrice: null,
        discount: null,
        rating: 0,
        reviews: 0,
        image: "https://admins.junisaproducts.com/images/product/0m3eDlywUXaUsL4pZTt2D33s2Pfr8YoloetGDltI.jpg",
        badge: null,
        inStock: true
      }
    ],
    customStyles: {
      container: {
        padding: "p-4 md:p-10",
        margin: null,
        borderRadius: "rounded-lg",
        boxShadow: null,
        background: "bg-gray-100",
        titleColor: "black",
        subtitleColor: "black",
        fontFamily: "Poppins",
        fontSize: "1em",
        featuredBg: "rgb(240, 90, 42)",
        activeBg: "green",
        accentColor: "#f05a2a",
        titlefontSize: "text-4xl md:text-5xl",
        titlefontWeight: "font-bold"
      },
      note: "This is a copy"
    },
    grid: {
      mobileCols: 2,
      desktopCols: 6
    },
    imageSize: {
      width: 200,
      height: 160
    },
    cardHeight: 470,
    productLinkPattern: "/products/{id}",
    devMode: true
  }
};

function SkeletonSection({ height = "300px", className = "" }) {
  return (
    <div className={`w-full animate-pulse bg-gray-100 ${className}`} style={{ minHeight: height }}>
     {/* <div className="h-full flex items-center justify-center text-gray-400 text-sm">
        Loading section...
      </div>*/}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────

function BalancesLayout() {
  const [isAdminMode, setIsAdminMode] = useState(false);
   const [isAdmin, setIsAdmin] = useState(false);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("token");
const { data: layout = {}, isLoading, error } = useQuery({
  queryKey: ['layouthome'],
  queryFn: fetchLayoutSettings,
  staleTime: 1000 * 60 * 60,        // 1 hour
  gcTime: 1000 * 60 * 60 * 24,      // 24 hours (was cacheTime in v4)
});

const CACHE_KEY_LAYOUT = 'layout_settings_cache_shopp';

  const [sectionOrder, setSectionOrder] = useState(layout?.freshOrder ||DEFAULT_LAYOUT_ORDER);
  const [globalConfigs, setGlobalConfigs] = useState(DEFAULT_GLOBAL_CONFIGS);
    const [searchConfig, setSearchConfig] = useState(null);
     const [CatConfig, setCatConfig] = useState({});
  const [cartConfig, setCartConfig] = useState(null);
  const [customConfigs, setCustomConfigs] = useState({});
  const [customSettings,setCustomSettings]=useState({});

  const [editingSlug, setEditingSlug] = useState(null);
  const [dataVersion, setDataVersion] = useState(0);

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addSuccess, setAddSuccess] = useState(null);

  const system_role = localStorage.getItem('system_role');
////console.log(userInfo);

   const availableSections = [
   // { id: 'navbar', name: 'Navigation Bar', icon: LayoutDashboard, color: 'bg-indigo-100 text-indigo-600' },
    { id: 'flashbar', name: 'Flash Bar', icon: Zap, color: 'bg-amber-100 text-amber-600' },
    { id: 'categories', name: 'Categories', icon: Grid, color: 'bg-green-100 text-green-600' },
    { id: 'products', name: 'Products', icon: ShoppingBag, color: 'bg-blue-100 text-blue-600' },
    { id: 'footer', name: 'Footer', icon: Footprints, color: 'bg-gray-100 text-gray-600' },
    { id: 'banner', name: 'Banner', icon: ImageIcon, color: 'bg-purple-100 text-purple-600' },
    { id: 'hero', name: 'Hero', icon: Star, color: 'bg-pink-100 text-pink-600' },
  ];

  const hasFetched = useRef(false);

  const CACHE_KEY = 'cms-layouts-490040-'+dataVersion;
  const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
//const CACHE_TTL = 1000;
  
 const loadMissingCustom = () => {
  //console.log('Here yes',sectionOrder?.length);
  setSectionOrder(layout?.freshOrder);
       if (sectionOrder?.length === 0) return;

    const customSlugs = layout?.freshOrder.filter(slug => slug.includes('-'));

    if (customSlugs?.length === 0) return;
//console.log('slugs',customSlugs);
    // Only fetch those we don't already have loaded
    const missing = customSlugs?.filter(slug => !layout?.customSettings[slug]);
//console.log('missing',layout?.customSettings);
    if (missing?.length === 0) return;
      try {
        const newCustom = { ...layout?.customSettings };

       // await Promise.all(
          missing?.map(async (slug) => {
            try {
              newCustom[slug]=customSettings[slug];
              //console.log("Here mow",customSettings['testimonials-2'])
            // console.log("Cached",JSON.parse(localStorage.getItem("sc")));
            /**  const res = await axios.get(
                `${config.API_URL}/api/customsettings/${slug}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                  withCredentials: true,
                }
              );


              if (res.data?.customSettings) {
                newCustom[slug] = res.data.customSettings;
              } else if (res.data?.settings) {
                // fallback - some endpoints might return "settings" key
                newCustom[slug] = res.data.settings;
              }**/
            } catch (err) {
              //console.warn(`Failed to load custom config for ${slug}:`, err);
            }
          })
       // );

        if (Object.keys(newCustom)?.length > Object.keys(layout?.customSettings)?.length) {
          setCustomConfigs(newCustom);

          // Update cache with new custom configs
          const currentCache = localStorage.getItem(CACHE_KEY);
          if (currentCache) {
            try {
              const parsed = JSON.parse(currentCache);
              localStorage.setItem(CACHE_KEY, JSON.stringify({
                ...parsed,
                data: {
                  ...parsed.data,
                  custom: newCustom
                },
                ts: Date.now()
              }));
            } catch {}
          }
        }
      } catch (err) {
        //console.error("Batch load of custom configs failed", err);
      }
    };




useEffect(() => {

  if (!layout?.freshOrder) return;   // safety
//console.log(layout?.customSettings['testimonials-2']);
  setSectionOrder(layout.freshOrder);
  setGlobalConfigs(layout.freshGlobal);
  setCatConfig(layout.categoriesnav_2 ?? {});
  setCustomSettings(layout.customSettings);
  setCustomConfigs(layout.customSettings);
  loadMissingCustom();
}, [layout]);

  // ── Background data loading ────────────────────────────────────────────────
/**useEffect(() => {
  if (hasFetched.current) return;
  hasFetched.current = true;

  const loadData = async () => {
    // ================================================================
    // 1. ALWAYS load whatever is in cache FIRST (stale is OK)
    //    → this is what prevents you from seeing DEFAULT_ configs
    // ================================================================
    const cachedStr = localStorage.getItem(CACHE_KEY);
    let hasStaleCache = false;
    let staleTs = 0;

    if (cachedStr) {
      try {
        const parsed = JSON.parse(cachedStr);
        const { data, ts } = parsed;

        staleTs = ts || 0;
        hasStaleCache = true;

        setSectionOrder(data.order || DEFAULT_LAYOUT_ORDER);
        setGlobalConfigs((prev) => ({ ...prev, ...data.global || {} }));
        setCustomConfigs(data.custom || {});
      } catch (e) {
        //console.warn("Invalid cache format, clearing");
        localStorage.removeItem(CACHE_KEY);
      }
    }

 

    // ================================================================
    // 2. Only fetch fresh data when cache is expired OR missing
    // ================================================================
    const isExpired = !hasStaleCache || Date.now() - staleTs >= CACHE_TTL;

    if (!isExpired) {
      // Cache is still fresh → we are done
      return;
    }

    // Cache is stale or missing → revalidate
    try {
     // await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });

      const [layoutRes] = await Promise.allSettled([
        axios.get(`${config.API_URL}/api/layoutsettings`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }),
      ]);

      // If the API call failed but we have stale data → keep the stale data
      if (layoutRes.status !== "fulfilled") {
        //console.warn("API fetch failed – keeping last cached data");
        return;
      }

      // Build fresh global config (same logic you already had)
      const freshGlobal = { ...DEFAULT_GLOBAL_CONFIGS };

      freshGlobal.flashbar = layoutRes.value.data?.flashBarSettings ?? freshGlobal.flashbar;
      freshGlobal.navbar = layoutRes.value.data?.navBarSettings ?? freshGlobal.navbar;
      freshGlobal.banner = layoutRes.value.data?.bannerSettings ?? freshGlobal.banner;
      freshGlobal.hero = layoutRes.value.data?.heroSettings ?? freshGlobal.hero;
      freshGlobal.categoriesnav_2 = layoutRes.value.data?.catSettings ?? freshGlobal.categoriesnav_2;
      freshGlobal.categories = layoutRes.value.data?.categorySettings ?? freshGlobal.categories;
      freshGlobal.products = layoutRes.value.data?.productSettings ?? freshGlobal.products;
      freshGlobal.footer = layoutRes.value.data?.footerSettings ?? freshGlobal.footer;

      const freshOrder = layoutRes.value.data?.order || DEFAULT_LAYOUT_ORDER;
//setCustomSettings(layoutRes.value.data?.customSettings);


      // Update UI with fresh data
      setSectionOrder(freshOrder);
      setGlobalConfigs(freshGlobal);

      // Keep your existing search/cart cache handling
      const otherCache = getCachedLayout();
      setSearchConfig(otherCache?.search || null);
       setCatConfig(layoutRes.value.data?.catSettings ?? {});
      setCartConfig(otherCache?.cart || null);
  

      // Cache the fresh result (24 h TTL)
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: {
            order: freshOrder,
            global: freshGlobal,
            custom: customConfigs, // will be enriched by the second useEffect
          },
          ts: Date.now(),
        })
      );
    } catch (err) {
      //console.warn("Background data load failed – keeping last cached data", err);
      // IMPORTANT: we NEVER clear the cache on error
    }
  };

  loadData();
}, [token, dataVersion]);   **/



    // ── Load missing custom/variant configs when sectionOrder is known ───────
  
   


   



 const getCachedLayout = () => {
    const item = localStorage.getItem(CACHE_KEY_LAYOUT);
    if (!item) return null;
    try {
      const { data, ts } = JSON.parse(item);
      if (Date.now() - ts < CACHE_TTL) return data;
      localStorage.removeItem(CACHE_KEY_LAYOUT);
      return null;
    } catch {
      localStorage.removeItem(CACHE_KEY_LAYOUT);
      return null;
    }
  };

  // ── Add New Section Handler ────────────────────────────────────────────────
  const handleAdd = async () => {
    if (!selectedSection) return;

    setAdding(true);
    setAddError(null);
    setAddSuccess(null);

    try {
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      if (!xsrfToken) throw new Error("No XSRF token");

      const response = await axios.post(
        `${config.API_URL}/api/addNewSection`,
        { section: selectedSection },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken),
            "Authorization": `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (response.data.status === 1) {
        setAddSuccess(`Added ${selectedSection} (${response.data.new_slug || 'new section'})`);
        setTimeout(() => {
          setIsAddModalOpen(false);
          setSelectedSection(null);
          setAddSuccess(null);
          localStorage.removeItem(CACHE_KEY);
          setDataVersion(v => v + 1);
        }, 1800);
      } else {
        setAddError(response.data.message || 'Failed to add section');
      }
    } catch (error) {
      //console.error("Add section error:", error);
      setAddError(error.response?.data?.message || "Network error. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  // ── Duplicate Section ──────────────────────────────────────────────────────
  const duplicateSection = async (originalSlug) => {
    try {
      const baseType = originalSlug.split('-')[0];

      let originalConfig;
      if (originalSlug.includes('-')) {
        originalConfig = customConfigs[originalSlug];
      } else {
        originalConfig = globalConfigs[baseType === 'product-view' ? 'product-view' : baseType];
      }

      if (!originalConfig) {
        alert("Cannot duplicate - no configuration found.");
        return;
      }

      const nextNumber = getNextDuplicateNumber(baseType);
      const newSlug = `${baseType}-${nextNumber}`;

      const newConfig = JSON.parse(JSON.stringify(originalConfig));

      if (newConfig.customStyles) {
        newConfig.customStyles.note = "This is a copy";
      }

      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      if (!xsrfToken) throw new Error("No XSRF token");

      const capitalized = baseType.charAt(0).toUpperCase() + baseType.slice(1);
      const saveEndpoint = `${config.API_URL}/api/saveCustomSettings/${capitalized}/${newSlug}`;

      const saveRes = await axios.post(saveEndpoint, { settings: newConfig }, {
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-XSRF-TOKEN": decodeURIComponent(xsrfToken),
          "Authorization": `Bearer ${token}`,
        },
        withCredentials: true,
      });

      if (saveRes.data.status !== 1) {
        throw new Error("Failed to save new section");
      }

      const newOrder = [...sectionOrder];
      const originalIndex = newOrder.indexOf(originalSlug);
      newOrder.splice(originalIndex + 1, 0, newSlug);

      await axios.post(config.API_URL + "/api/saveLayoutSettings", { order: newOrder }, {
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-XSRF-TOKEN": decodeURIComponent(xsrfToken),
          "Authorization": `Bearer ${token}`,
        },
        withCredentials: true,
      });

      setSectionOrder(newOrder);
      localStorage.removeItem(CACHE_KEY);
      alert(`Duplicated as ${newSlug}!`);
    } catch (error) {
      //console.error("Duplication failed:", error);
      alert("Failed to duplicate section.");
    }
  };

  // ── Delete Section ─────────────────────────────────────────────────────────
  const deleteSection = async (slugToDelete) => {
    if (!window.confirm(`Delete "${slugToDelete}"? This cannot be undone.`)) return;

    try {
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      if (!xsrfToken) throw new Error("No XSRF token");

      await axios.get(
        `${config.API_URL}/api/deleteSection/${slugToDelete}`,
        {
          headers: {
            "Authorization": `Bearer ${token}`,
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken),
          },
          withCredentials: true,
        }
      );

      const newOrder = sectionOrder?.filter(s => s !== slugToDelete);
      setSectionOrder(newOrder);
      localStorage.removeItem(CACHE_KEY);
      alert('Section deleted!');
    } catch (error) {
      //console.error("Delete failed:", error);
      alert("Failed to delete section.");
    }
  };

  // ── Move Section ───────────────────────────────────────────────────────────
  const moveSection = (fromIndex, direction) => {
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= sectionOrder?.length) return;

    const newOrder = [...sectionOrder];
    const [moved] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, moved);
    setSectionOrder(newOrder);
  };

  // ── Save Layout Order ──────────────────────────────────────────────────────
  const saveLayout = async () => {
    try {
      setSaving(true);
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      if (!xsrfToken) throw new Error("No XSRF token");

      const res = await axios.post(
        config.API_URL + "/api/saveLayoutSettings",
        { order: sectionOrder },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken),
            "Authorization": `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (res.data.status === 1) {
        alert("Layout saved!");
        localStorage.removeItem(CACHE_KEY);
      } else {
        alert("Save failed.");
      }
    } catch (error) {
      alert("Failed to save layout.");
    } finally {
      setSaving(false);
    }
  };

  const getNextDuplicateNumber = (baseType) => {
    const existingNumbers = sectionOrder
      .filter(s => s.startsWith(baseType + '-'))
      .map(s => {
        const match = s.match(/-(\d+)$/);
        return match ? parseInt(match[1], 10) : 1;
      });
    return existingNumbers.length === 0 ? 2 : Math.max(...existingNumbers) + 1;
  };


  // ── Render section with skeleton fallback ──────────────────────────────────
   // ── Render section with skeleton fallback for loading customs ────────────
  const renderSection = useCallback((slug) => {
    const baseType = slug.split('-')[0];
    const isCustom = slug.includes('-');

    // Get config: prefer custom → global → empty object
    const config = isCustom
      ? (customConfigs[slug] ?? globalConfigs[baseType] ?? {})
      : (globalConfigs[baseType] ?? {});

    // If it's a custom slug and we still don't have data → show skeleton
   /** if (isCustom && !customConfigs[slug] && !globalConfigs[baseType]) {
      return <SkeletonSection height="180px" className="bg-gray-100" />;
    }**/

    switch (baseType.toLowerCase()) {
      case 'flashbar':
        return <FlashBar config={config} />;
      case 'navbar':
        if(CatConfig.length>0){
           return <NavigationBar config={config} searchConfig={searchConfig} CatConfig={CatConfig} />;
        }else{
           return <NavigationBar config={config} searchConfig={searchConfig} CatConfig={CatConfig} />;
        }
       
      case 'banner':
        return <Banner config={config} />;
      case 'hero':
        return <HeroSection config={config} />;
      case 'categories':
        return <CategorySection config={config} />;
      case 'products':
        return <ProductSection config={config} slug={slug} />;
      case 'testimonials':
        return <Testimonials config={config} />;
         case 'cta':
        return <Cta config={config} />;
         //case 'categoriesnav':
       // return <CategoriesNav config={config} />;
         case 'faqs':
        return <Faqs config={config} />;
          case 'christmas':
        return <Christmas config={config} />;
          case 'marketingmodals':
        return <ModalSection config={config} />;
      case 'cart':
        return <CartSection config={config} isOpen={false} onClose={() => {}} />;
      case 'footer':
        return <FooterSection config={config} />;
      case 'product-view':
        return <ProductView config={config} />;
      default:
        return ;
    }
  }, [globalConfigs, customConfigs, searchConfig]);

    // ── Force refresh from server ─────────────────────────────────────────────
  const handleManualRefresh = useCallback(() => {
    localStorage.removeItem(CACHE_KEY);
    setDataVersion(prev => prev + 1);
    window.location.reload();
  }, []);


  // ── Admin editing modal ────────────────────────────────────────────────────
  if (editingSlug) {
    const baseType = editingSlug.split('-')[0];
    const isCustom = editingSlug.includes('-');

    const AdminComponents = {
      flashbar: FlashBarAdmin,
      navbar: NavigationAdmin,
      hero: HeroAdmin,
      banner: BannerAdmin,
      categories: CategoryAdmin,
      products: ProductAdmin,
      cart: CartAdmin,
      footer: FooterAdmin,
      search: SearchAdmin,
      testimonials: TestimonialsAdmin,
       cta: CtaAdmin,
      categoriesnav: CategoriesNavAdmin,
      faqs: FaqAdmin,
      marketingmodals: ModalAdmin,
      christmas: ChristmasAdmin,
      "product-view": ProductViewAdmin,
    };

    const AdminComponent = AdminComponents[baseType];

    if (!AdminComponent) {
      return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-white p-10 rounded-xl shadow-2xl">
            <p className="text-xl text-red-600">No editor available for: {baseType}</p>
          </div>
        </div>
      );
    }

    const currentConfig = isCustom 
      ? (customConfigs[editingSlug] || null)
      : (globalConfigs[baseType] || null);

    return (
      <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center overflow-auto pt-12 pb-12">
        <div className="bg-white w-full max-w-9xl rounded-xl shadow-2xl overflow-hidden mx-4">
          <div className="p-6 border-b flex justify-between items-center bg-gray-50">
            <h2 className="text-2xl font-bold capitalize">
              Edit {baseType.replace('-', ' ')} ({editingSlug})
            </h2>
            <button 
              onClick={() => setEditingSlug(null)}
              className="text-3xl text-gray-600 hover:text-gray-900"
            >
              ×
            </button>
          </div>
          <div className="p-6">
            <React.Suspense fallback={<div className="text-center py-12">Loading editor...</div>}>
              <AdminComponent 
                config={currentConfig || {}}
                slug={editingSlug}
                onSave={(updated) => {
                  if (isCustom) {
                    setCustomConfigs(prev => ({ ...prev, [editingSlug]: updated }));
                  } else {
                    setGlobalConfigs(prev => ({ ...prev, [baseType]: updated }));
                  }
                  localStorage.removeItem(CACHE_KEY);
                  setEditingSlug(null);
                }}
                onClose={() => setEditingSlug(null)}
              />
            </React.Suspense>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
<style>{`
       @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&family=Quicksand:wght@400;500;600;700&display=swap');
     html,body{
       font-family: ${config.FONT_FAMILY}, sans-serif !important;
     }
      `}
      </style>
      {/* Admin controls */}
      <div className="fixed top-5 left-5 z-50 flex flex-wrap gap-3">
       {/* 
       {system_role=="Admin" && (

        <button
          onClick={() => setIsAdminMode(!isAdminMode)}
          className={`px-6 py-3 rounded-full font-bold shadow-xl transition-colors ${
            isAdminMode 
              ? 'bg-red-600 hover:bg-red-700 text-white' 
              : 'bg-emerald-600 hover:bg-emerald-700 text-white'
          }`}
        >
          {isAdminMode ? 'Exit Edit Mode' : 'Enter Edit Mode'}
        </button>
        )}*/}

        {isAdminMode && (
          <>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-full font-bold shadow-xl transition-all flex items-center gap-2"
            >
              <Plus size={20} />
              Add Section
            </button>

            <button
              onClick={handleManualRefresh}
              className="px-5 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-full font-bold shadow-xl transition-all flex items-center gap-2"
              title="Force reload all data from server"
            >
              <RefreshCw size={18} />
              Refresh Data
            </button>
          </>
        )}
      </div>

      {/* Main content - renders from defaults instantly */}
      {sectionOrder?.map((slug, index) => (
        <div key={slug} className="relative">
          {renderSection(slug)}

          {1==1 && (
          <> 
           {isAdminMode && (
            <div className="absolute top-16  right-2 bg-white/90 backdrop-blur p-3 rounded-lg shadow-xl flex gap-2 z-50 flex-wrap">
              <button onClick={() => setEditingSlug(slug)} className="p-2 bg-green-50 hover:bg-green-100 text-green-700 rounded text-sm">Edit</button>
              <button onClick={() => duplicateSection(slug)} className="p-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded text-sm">Duplicate</button>
              <button onClick={() => deleteSection(slug)} className="p-2 bg-red-50 hover:bg-red-100 text-red-700 rounded text-sm">Delete</button>
              <button onClick={() => moveSection(index, -1)} disabled={index === 0} className="p-2 hover:bg-gray-100 rounded disabled:opacity-50 text-sm">↑</button>
              <button onClick={() => moveSection(index, 1)} disabled={index === sectionOrder?.length - 1} className="p-2 hover:bg-gray-100 rounded disabled:opacity-50 text-sm">↓</button>
            </div>
          )}
          </>
          )}

            {(slug.trim().toLowerCase()=='navbar' || slug.trim().toLowerCase()=='flashbar') && (
          <> 
           {isAdminMode && (
            <div className="absolute top-2 right-2 bg-white/90 backdrop-blur p-3 rounded-lg shadow-xl flex gap-2 z-50 flex-wrap">
              <button onClick={() => setEditingSlug(slug)} className="p-2 bg-green-50 hover:bg-green-100 text-green-700 rounded text-sm">Edit</button>
              <button onClick={() => duplicateSection(slug)} className="p-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded text-sm">Duplicate</button>
              <button onClick={() => deleteSection(slug)} className="p-2 bg-red-50 hover:bg-red-100 text-red-700 rounded text-sm">Delete</button>
              <button onClick={() => moveSection(index, -1)} disabled={index === 0} className="p-2 hover:bg-gray-100 rounded disabled:opacity-50 text-sm">↑</button>
              <button onClick={() => moveSection(index, 1)} disabled={index === sectionOrder?.length - 1} className="p-2 hover:bg-gray-100 rounded disabled:opacity-50 text-sm">↓</button>
            </div>
          )}
          </>
          )}
        </div>
      ))}

      {/* Save layout bar */}
      {isAdminMode && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur-lg px-10 py-5 rounded-full shadow-2xl flex items-center gap-8 z-50 border border-gray-200">
          <button
            onClick={() => {
              saveLayout();
              setSaving(true);
              setTimeout(() => setSaving(false), 1200);
            }}
            disabled={saving}
            className="bg-green-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-green-700 disabled:opacity-60 transition"
          >
            {saving ? 'Saving...' : 'Save Layout Order'}
          </button>
          <span className="text-gray-600 text-sm">↑↓ reorder • Edit • Duplicate • Delete</span>
        </div>
      )}


       {/* Add New Section Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-8 py-5 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold text-gray-900">Add New Section</h2>
              <button onClick={() => setIsAddModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                <X size={28} />
              </button>
            </div>

            <div className="p-8">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 mb-10">
                {availableSections?.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedSection(type.id)}
                    className={`flex flex-col items-center p-6 border-2 rounded-xl transition-all duration-200 ${
                      selectedSection === type.id
                        ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-300'
                        : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${type.color}`}>
                      <type.icon size={32} />
                    </div>
                    <span className="font-medium text-gray-800 text-center">{type.name}</span>
                  </button>
                ))}
              </div>

              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdd}
                  disabled={!selectedSection || adding}
                  className={`px-8 py-3 rounded-lg font-medium flex items-center gap-2 ${
                    selectedSection && !adding
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {adding ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white"></div>
                      Adding...
                    </>
                  ) : (
                    'Add Section'
                  )}
                </button>
              </div>

              {addError && <p className="mt-6 text-red-600 text-center">{addError}</p>}
              {addSuccess && <p className="mt-6 text-green-600 text-center">{addSuccess}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BalancesLayout;